import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ASSET_DOCUMENT_PARAM,
  AssetVersionBadges,
  AssetWorkspaceHeader,
} from '@/layouts/AssetWorkspaceLayout';
import {
  V1AutomationStage,
  type V1Automation,
} from '@/generated/hooks/grimoire/models';
import type { V1Run } from '@/generated/hooks/jeeves/models';
import { AUTOMATION_RUN_STAGE_PARAM } from '@/modules/automation/constants';
import type { IVersionHistoryItemData } from '@/modules/automation-details/components/version-history/types';
import { getAutomationLifecycle } from '@/modules/automations/lifecycle';
import {
  getRunActivityTone,
  useAutomationActivitySummary,
} from '@/modules/automation/hooks/use-automation-activity-summary';
import type { TAutomationVersionView } from '@/modules/automation/components/AutomationVersionSelect';
import { getRoutePath } from '@/routes/util';
import { AutomationStageBadge } from '@/shared/components/AutomationStageBadge';
import { useBackNavigation } from '@/shared/hooks/use-back-navigation';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import {
  formatRelativeTime,
  getTimestamp,
} from '@/shared/utils/datetime-utils';

/**
 * The more recent of two runs.
 *
 * @param a - A run, if any
 * @param b - Another run, if any
 * @returns Whichever started last
 */
function getLatestRun(a?: V1Run, b?: V1Run): V1Run | undefined {
  if (!a || !b) return a ?? b;
  return getTimestamp(a.create_time) >= getTimestamp(b.create_time) ? a : b;
}

/**
 * The automation's Activity route. It opens on Draft when only draft runs are
 * waiting on the user, so the pill's count is what the banner shows, and
 * carries the version Definition was showing so its way back returns there.
 *
 * @param params - The route's org, workspace and automation
 * @param livePending - Pending exceptions on live runs
 * @param draftPending - Pending exceptions on draft runs
 * @param document - The version Definition was showing, if it says
 * @returns The Activity href
 */
export function getAutomationActivityHref(
  params: { orgId: string; workspaceId: string; draftId: string },
  livePending: number,
  draftPending: number,
  document?: TAutomationVersionView,
): string {
  const search = new URLSearchParams();
  // From the draft, Activity opens on its runs; otherwise on whichever stage
  // has work waiting.
  if (
    document === 'draft' ||
    (!document && livePending === 0 && draftPending > 0)
  ) {
    search.set(AUTOMATION_RUN_STAGE_PARAM, 'draft');
  }
  if (document) search.set(ASSET_DOCUMENT_PARAM, document);
  const href = getRoutePath('DRAFT_ACTIVITY', params);
  // Not `search.size`, which older browsers (Safari < 17) don't have.
  const query = search.toString();
  return query ? `${href}?${query}` : href;
}

interface IAutomationWorkspaceHeaderProps {
  automationId: string;
  title: string;
  /** Its versions, for the stage badge beside the name. */
  automation?: Pick<V1Automation, 'version' | 'latest_published_version'>;
  /** Whether a draft exists. A live automation without one shows as Live. */
  hasDraft?: boolean;
  /** Holds the stage badges' place until the versions behind them are known. */
  isStageLoading?: boolean;
  /** Overrides the Definition route, e.g. the published document when there's no draft. */
  definitionHref?: string;
  view: 'definition' | 'activity';
  /**
   * The version Definition shows, or on Activity the one it came from. On
   * Definition the document's own badge switches versions, so there is no
   * live link, and Activity's way back reads "Document" for the live one and
   * "Draft" for the draft.
   */
  versionView?: TAutomationVersionView;
  /** After the name and badges, e.g. the test workbench and map toggles. */
  titleActions?: ReactNode;
  /** Before the view switch, e.g. Run and Publish on Activity. */
  actions?: ReactNode;
  /** The version open in version history; its badge replaces the stage badges. */
  viewingVersion?: Pick<IVersionHistoryItemData, 'revisionId' | 'stage'>;
  /** Replaces the way back to the catalog, e.g. leaving version history. */
  back?: { label: string; onClick: () => void };
}

/**
 * A draft automation's workspace header, on Definition and Activity: back to
 * the automations catalog, its name, and the switch between the two views. On
 * Definition the switch carries what needs the user (exceptions to approve, or
 * a failed last run) or its latest run, live or draft. A draft of a live
 * automation links to its published document unless the document switches
 * between them itself (`versionView`).
 */
export function AutomationWorkspaceHeader({
  automationId,
  title,
  automation,
  hasDraft = true,
  isStageLoading = false,
  definitionHref,
  view,
  versionView,
  titleActions,
  actions,
  viewingVersion,
  back,
}: IAutomationWorkspaceHeaderProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  // Both stages: a never-published draft still has draft runs to show, and a
  // live automation's exceptions need approval whichever version is open.
  const live = useAutomationActivitySummary(automationId, false);
  const draft = useAutomationActivitySummary(automationId, true);
  const pendingCount = live.pendingCount + draft.pendingCount;
  const latestRun = getLatestRun(live.latestRun, draft.latestRun);
  const latestRunTime = latestRun?.create_time;
  const lifecycle = getAutomationLifecycle({
    version: automation?.version,
    latest_published_version: automation?.latest_published_version,
  });

  const hasBothVersions = hasDraft && Boolean(lifecycle.liveVersion);
  const showLiveLink =
    view === 'definition' && !versionView && hasBothVersions && !viewingVersion;

  const { canGoBack, goBack } = useBackNavigation(
    getRoutePath('DRAFTS', { orgId, workspaceId }),
  );

  const getViewingVersionLabel = ({
    revisionId,
    stage,
  }: NonNullable<typeof viewingVersion>) => {
    if (revisionId === automation?.latest_published_version) {
      return tCommon('common.assetStage.liveVersion', { version: revisionId });
    }
    return stage === V1AutomationStage.AUTOMATION_STAGE_PUBLISHED
      ? tCommon('common.assetStage.publishedVersion', { version: revisionId })
      : tCommon('common.assetStage.draftVersion', { version: revisionId });
  };

  return (
    <AssetWorkspaceHeader
      back={
        back ?? {
          label: canGoBack
            ? tCommon('common.assetWorkspace.back')
            : tAutomation('automation.header.tooltip.backToAutomations'),
          onClick: goBack,
        }
      }
      title={title}
      badges={
        automation && viewingVersion ? (
          <AutomationStageBadge
            stage="viewing"
            label={getViewingVersionLabel(viewingVersion)}
            className="ml-1 shrink-0"
            data-testid="automation-workspace-viewing-version"
          />
        ) : // On Definition the document names its version.
        view === 'activity' && (automation || isStageLoading) ? (
          <AssetVersionBadges
            state={hasDraft ? lifecycle.state : 'live'}
            isLoading={isStageLoading}
          />
        ) : undefined
      }
      view={view}
      activityHref={getAutomationActivityHref(
        { orgId, workspaceId, draftId: automationId },
        live.pendingCount,
        draft.pendingCount,
        view === 'definition' ? versionView : undefined,
      )}
      definitionIsLive={
        versionView
          ? versionView === 'live'
          : Boolean(lifecycle.liveVersion) || !hasDraft
      }
      definitionHref={
        definitionHref ??
        getRoutePath('DRAFT_EDITOR', {
          orgId,
          workspaceId,
          draftId: automationId,
        })
      }
      attention={
        pendingCount > 0
          ? {
              label: tAutomation('automation.workspace.waitingCount', {
                count: pendingCount,
              }),
              tone: 'attention',
            }
          : latestRun?.state?.failed
            ? {
                label: tAutomation('automation.workspace.lastRunFailed'),
                tone: 'error',
              }
            : undefined
      }
      latest={
        latestRun && latestRunTime
          ? {
              label: tAutomation('automation.workspace.lastRun', {
                when: formatRelativeTime(latestRunTime),
              }),
              tone: getRunActivityTone(latestRun),
            }
          : undefined
      }
      liveHref={
        showLiveLink
          ? getRoutePath('AUTOMATION_AOP', { orgId, workspaceId, automationId })
          : undefined
      }
      titleActions={titleActions}
      actions={actions}
      data-testid="automation-workspace-header"
    />
  );
}
